/**
 * The firm's own engagement-letter templates.
 *
 * ===========================================================================
 * WHAT THIS REPLACES
 * ===========================================================================
 * Until this screen existed, `SendEngagementDialog` offered one empty textarea and a
 * comment admitting it: "Firms paste their standard wording from Word the first few
 * times; templates come later and will fill this in." The backend model
 * (`AgreementTemplate`) and the rendering it feeds (`agreements.draft`) were built and
 * tested long before this screen was — see CURRENT_STATUS.md section 7. This is the
 * "templates come later" screen.
 *
 * ===========================================================================
 * WHY DRAFT / ACTIVE
 * ===========================================================================
 * A template that is half-written should not be one keystroke away from becoming
 * somebody's engagement letter. `draft` templates are editable here but excluded from
 * the picker in `SendEngagementDialog` (`GET agreements/templates/`) — only `active`
 * ones are offered. There is no separate "publish" action: moving the status field is
 * the whole of it.
 *
 * ===========================================================================
 * WHY THIS SCREEN IS ADMINISTRATOR-ONLY WHILE THE PICKER IS NOT
 * ===========================================================================
 * Managing templates is gated on the `template` capability — the same one already
 * defined in the catalogue for services, workflows and forms, and today held only by
 * the Organization Administrator role. *Using* a template while drafting a letter is a
 * different, much more widely held permission (`agreement.view` / `agreement.create`),
 * which is why the picker lives on the agreements endpoint, not this one. See the build
 * report for the reasoning in full.
 */

import {
  ConflictError,
  STAFF,
  qk,
  useApi,
  useApiMutation,
  usePaginatedList,
  type AgreementTemplate,
  type AgreementTemplateStatus,
} from '@legalcrm/api';
import {
  Badge,
  Button,
  Card,
  CardBody,
  ConfirmModal,
  Input,
  Modal,
  Select,
  Table,
  Textarea,
  useToast,
  type Column,
} from '@legalcrm/ui';
import { useState } from 'react';

import { Can } from '@/auth/permissions';

const STATUS_OPTIONS: Array<{ value: AgreementTemplateStatus; label: string }> = [
  { value: 'draft', label: 'Draft — being written' },
  { value: 'active', label: 'Active — offered when drafting a letter' },
];

export function AgreementTemplatesPage() {
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<AgreementTemplate | null>(null);
  const [removing, setRemoving] = useState<AgreementTemplate | null>(null);

  const list = usePaginatedList<AgreementTemplate>(
    qk.list('agreement-templates'),
    STAFF.agreementTemplates,
    { limit: 100 },
  );

  const columns: Column<AgreementTemplate>[] = [
    {
      key: 'name',
      header: 'Template',
      render: (row) => (
        <div>
          <span className="text-ink">{row.name}</span>
          <code className="ml-2 rounded bg-surface-sunken px-1.5 py-0.5 text-xs text-ink-muted">
            {row.key}
          </code>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) =>
        row.status === 'active' ? (
          <Badge tone="success">Active</Badge>
        ) : (
          <Badge tone="internal">Draft</Badge>
        ),
    },
    {
      key: 'updated_at',
      header: 'Last edited',
      hideOnMobile: true,
      render: (row) => new Date(row.updated_at).toLocaleDateString(),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (row) => (
        <Can module="template" action="edit">
          <div className="flex justify-end gap-1">
            <Button variant="ghost" size="sm" onClick={() => setEditing(row)}>
              Edit
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setRemoving(row)}>
              Remove
            </Button>
          </div>
        </Can>
      ),
    },
  ];

  return (
    <>
      <Card className="mb-4">
        <CardBody>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-ink-muted">
              Saved wording a fee earner can pick instead of pasting the letter in by
              hand. Only <strong className="text-ink">active</strong> templates are
              offered while drafting.
            </p>
            <Can module="template" action="create">
              <Button variant="primary" onClick={() => setCreating(true)}>
                New template
              </Button>
            </Can>
          </div>
        </CardBody>
      </Card>

      <Table
        caption="Engagement letter templates"
        columns={columns}
        rows={list.items}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        error={list.error}
        onRetry={() => void list.refetch()}
        emptyTitle="No templates yet"
        emptyDescription="Every engagement letter is still pasted in by hand. Add the firm's standard wording here once, and it is a pick rather than a paste from then on."
      />

      <TemplateDialog open={creating} onClose={() => setCreating(false)} template={null} />
      <TemplateDialog open={editing !== null} onClose={() => setEditing(null)} template={editing} />
      <RemoveTemplateDialog template={removing} onClose={() => setRemoving(null)} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Adding and editing
// ---------------------------------------------------------------------------

function TemplateDialog({
  open,
  onClose,
  template,
}: {
  open: boolean;
  onClose: () => void;
  template: AgreementTemplate | null;
}) {
  const api = useApi();
  const { toast } = useToast();
  const editingExisting = template !== null;

  const [name, setName] = useState('');
  const [body, setBody] = useState('');
  const [status, setStatus] = useState<AgreementTemplateStatus>('draft');
  // Which record the form was last filled from, so reopening on a different row
  // refills it — an effect keyed on `template` would fight the user's typing.
  const [filledFrom, setFilledFrom] = useState<string | null>(null);

  const identity = template?.id ?? (open ? 'new' : null);
  if (open && filledFrom !== identity) {
    setFilledFrom(identity);
    setName(template?.name ?? '');
    setBody(template?.body ?? '');
    setStatus(template?.status ?? 'draft');
  }

  const save = useApiMutation<AgreementTemplate, void>(
    {
      mutationFn: () => {
        const payload = { name, body, status };
        return template
          ? api.patch<AgreementTemplate>(`${STAFF.agreementTemplate(template.id)}/`, payload)
          : api.post<AgreementTemplate>(`${STAFF.agreementTemplates}/`, payload);
      },
      invalidates: [qk.all('agreement-templates')],
    },
    {
      onSuccess: (saved) => {
        toast({
          title: editingExisting ? 'Template updated' : 'Template added',
          description:
            saved.status === 'active'
              ? `${saved.name} — offered when drafting a letter.`
              : `${saved.name} — still a draft, not yet offered.`,
          tone: 'success',
        });
        onClose();
      },
      onError: (error) =>
        toast({
          title: 'The template was refused',
          description:
            error instanceof ConflictError ? error.detail : 'Try again.',
          tone: 'warning',
        }),
    },
  );

  const blocked = !name.trim() || !body.trim();

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={editingExisting ? `Edit ${template.name}` : 'New template'}
      description={
        editingExisting
          ? 'Editing this never reaches a letter already drafted from it — the text is copied once, at the moment somebody drafts.'
          : "The firm's standard wording, saved once and picked instead of pasted from then on."
      }
    >
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (!blocked) void save.mutateAsync();
        }}
      >
        <Input
          label="Name"
          value={name}
          onChange={(event) => setName(event.currentTarget.value)}
          required
          autoFocus
          autoComplete="off"
          hint="What a fee earner sees in the picker while drafting a letter."
        />

        <Textarea
          label="Wording"
          rows={14}
          value={body}
          onChange={(event) => setBody(event.currentTarget.value)}
          required
          hint="The full letter. Copied exactly onto the agreement the moment somebody drafts from it — editing it here later never reaches a letter already drafted."
        />

        <Select
          label="Status"
          value={status}
          onChange={(event) => setStatus(event.currentTarget.value as AgreementTemplateStatus)}
          options={STATUS_OPTIONS}
          hint="Draft templates stay here for editing and are not offered when drafting a letter."
        />

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" loading={save.isPending} disabled={blocked}>
            {editingExisting ? 'Save template' : 'Add template'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Removing
// ---------------------------------------------------------------------------

/**
 * Soft on the server, like every other deletion in this system. Agreements already
 * drafted from this template keep their own frozen text (`rendered_body`) regardless —
 * removing the template cannot reach a letter that already exists.
 */
function RemoveTemplateDialog({
  template,
  onClose,
}: {
  template: AgreementTemplate | null;
  onClose: () => void;
}) {
  const api = useApi();
  const { toast } = useToast();

  const remove = useApiMutation<void, void>(
    {
      mutationFn: () => api.delete<void>(`${STAFF.agreementTemplate(template!.id)}/`),
      invalidates: [qk.all('agreement-templates')],
    },
    {
      onSuccess: () => {
        toast({
          title: 'Template removed',
          description: `${template?.name} is no longer offered when drafting a letter.`,
          tone: 'success',
        });
        onClose();
      },
    },
  );

  return (
    <ConfirmModal
      open={template !== null}
      onClose={onClose}
      title={`Remove ${template?.name ?? 'this template'}?`}
      confirmLabel="Remove template"
      destructive
      loading={remove.isPending}
      onConfirm={() => void remove.mutateAsync()}
      consequences={
        <p>
          It comes off the picker in the engagement letter dialog. Letters already
          drafted from it keep their own wording — nothing about them changes.
        </p>
      }
    />
  );
}
